import React, { useMemo, useRef } from 'react';
import { BookOpen, CheckCircle2, MessageSquarePlus, Paperclip } from 'lucide-react';
import { MinhaMesaFormFields } from './MinhaMesaFormFields';
import { MinhaMesaCompletionBar } from './MinhaMesaCompletionBar';
import { OperationalChecklist } from './OperationalChecklist';
import { ExecutionBlockRenderer } from './ExecutionBlockRenderer';
import { ExecutionTimelineSummary, type TimelineSummaryEvent } from './ExecutionTimelineSummary';
import type { TaskDetailItem } from '../../shared/types/task';
import type { BlueprintChecklistItem } from '../../../types/processBlueprint';

interface TaskExecutionWorkbenchProps {
  task: TaskDetailItem;
  hasBlocks: boolean;
  executionBlocks: any[];
  blockResponses: Record<string, unknown>;
  formState: Record<string, unknown>;
  visibleExecutionFields: any[];
  checklist: BlueprintChecklistItem[];
  checklistState: Record<string, boolean>;
  invalidFieldIds: string[];
  isTaskPaused: boolean;
  isTaskCompleted: boolean;
  notes: string;
  onNotesChange: (notes: string) => void;
  onFieldChange: (fieldId: string, value: unknown) => void;
  onChecklistToggle: (itemId: string) => void;
  onComplete: () => void;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onSaveNotes?: (note: string, files?: File[]) => void;
  isCompleting: boolean;
  detailLoading: boolean;
  canComplete: boolean;
  attachInputRef?: React.RefObject<HTMLInputElement | null>;
  observationsCount?: number;
  attachmentsCount?: number;
  timelineEvents?: TimelineSummaryEvent[];
  readHandoff?: string | null;
  readObservation?: { userName: string; message: string; ago: string } | null;
  progressPct?: number;
  instruction?: string | null;
  hasStarted?: boolean;
  activityOpen?: boolean;
  referenceOpen?: boolean;
  onToggleActivity?: () => void;
  onToggleReference?: () => void;
}

const FLOW_STEPS = ['Ler', 'Executar', 'Registrar', 'Concluir'] as const;

function resolveActiveStep(input: {
  completed: boolean;
  hasStarted: boolean;
  hasInstruction: boolean;
  canComplete: boolean;
  hasFormWork: boolean;
}): number {
  if (input.completed) return 4;
  if (!input.hasStarted) return input.hasInstruction ? 0 : 1;
  if (input.canComplete) return 3;
  return input.hasFormWork ? 2 : 1;
}

function RailToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide ${
        active
          ? 'bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900'
          : 'border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400'
      }`}
    >
      {label}
    </button>
  );
}

function FlowPipeline({ activeIndex }: { activeIndex: number }) {
  return (
    <nav className="flex items-center gap-1" aria-label="Fluxo de execução">
      {FLOW_STEPS.map((step, index) => {
        const isActive = index === activeIndex;
        const isDone = index < activeIndex;
        return (
          <React.Fragment key={step}>
            {index > 0 && <div className={`h-px w-4 ${isDone ? 'bg-emerald-400' : 'bg-slate-200 dark:bg-slate-700'}`} />}
            <span
              className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : isDone
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                    : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'
              }`}
            >
              {index + 1}. {step}
            </span>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export const TaskExecutionWorkbench: React.FC<TaskExecutionWorkbenchProps> = ({
  task,
  hasBlocks,
  executionBlocks,
  blockResponses,
  formState,
  visibleExecutionFields,
  checklist,
  checklistState,
  invalidFieldIds,
  isTaskPaused,
  isTaskCompleted,
  notes,
  onNotesChange,
  onFieldChange,
  onChecklistToggle,
  onComplete,
  onApprove,
  onReject,
  onSaveNotes,
  isCompleting,
  detailLoading,
  canComplete,
  attachInputRef,
  observationsCount = 0,
  attachmentsCount = 0,
  timelineEvents = [],
  readHandoff,
  readObservation,
  instruction,
  hasStarted = false,
  activityOpen = false,
  referenceOpen = false,
  onToggleActivity,
  onToggleReference,
}) => {
  const internalAttachRef = useRef<HTMLInputElement>(null);
  const fileRef = attachInputRef ?? internalAttachRef;
  const readOnly = isTaskPaused || isTaskCompleted;
  const hasFormContent = (hasBlocks && executionBlocks.length > 0) || visibleExecutionFields.length > 0;
  const hasChecklist = checklist.length > 0;
  const stageLabel = (task.stageName || 'esta etapa').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  const instructionText = instruction?.trim() || '';

  const activeStep = useMemo(
    () => resolveActiveStep({
      completed: isTaskCompleted,
      hasStarted,
      hasInstruction: Boolean(instructionText || readHandoff || readObservation),
      canComplete,
      hasFormWork: hasFormContent || hasChecklist,
    }),
    [isTaskCompleted, hasStarted, instructionText, readHandoff, readObservation, canComplete, hasFormContent, hasChecklist],
  );

  return (
    <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden bg-slate-50/50 dark:bg-[var(--bg-workspace)]">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 lg:px-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <FlowPipeline activeIndex={activeStep} />
            <div className="flex items-center gap-1">
              <RailToggle label="Atividade" active={activityOpen} onClick={onToggleActivity} />
              <RailToggle label="Referência" active={referenceOpen} onClick={onToggleReference} />
            </div>
          </div>

          {(readHandoff || readObservation) && (
            <section className="mb-5 rounded-lg border border-slate-200/80 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
              <div className="mb-2 flex items-center gap-2">
                <BookOpen size={13} className="text-blue-600" />
                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-500">Ler · contexto da atividade</span>
              </div>
              {readHandoff && (
                <p className="text-[12px] leading-relaxed text-slate-700 dark:text-slate-200">{readHandoff}</p>
              )}
              {readObservation && (
                <div className={readHandoff ? 'mt-2 border-t border-slate-100 pt-2 dark:border-slate-800' : ''}>
                  <p className="text-[9px] text-slate-400">
                    {readObservation.userName} · {readObservation.ago}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">
                    {readObservation.message}
                  </p>
                </div>
              )}
            </section>
          )}

          <section className="mb-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Executar · passos da atividade</h2>
              {hasChecklist && (
                <span className="text-[9px] font-medium text-slate-400">
                  {Object.values(checklistState).filter(Boolean).length}/{checklist.length} concluídos
                </span>
              )}
            </div>

            {hasChecklist ? (
              <OperationalChecklist
                items={checklist}
                checklistState={checklistState}
                onToggle={onChecklistToggle}
                readOnly={readOnly}
              />
            ) : !hasFormContent ? (
              <div className="rounded-lg border border-slate-200/80 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-900/40">
                <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">{stageLabel}</p>
                {instructionText ? (
                  <p className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">
                    {instructionText}
                  </p>
                ) : (
                  <p className="mt-2 text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">
                    {hasStarted
                      ? 'O trabalho desta etapa está em andamento. Não há campos obrigatórios. Quando terminar, conclua a etapa.'
                      : 'Inicie a execução para registrar o começo do trabalho. Esta etapa não pede preenchimento. Quando o trabalho estiver feito, conclua a etapa.'}
                  </p>
                )}
              </div>
            ) : null}

            {hasFormContent && (
              <div className={hasChecklist ? 'mt-5 border-t border-slate-200/80 pt-4 dark:border-slate-700' : ''}>
                {hasChecklist && (
                  <p className="mb-3 text-[9px] font-bold uppercase tracking-wider text-slate-400">
                    Dados da execução
                  </p>
                )}
                {hasBlocks && executionBlocks.length > 0 ? (
                  <ExecutionBlockRenderer
                    blocks={executionBlocks}
                    responses={{ ...blockResponses, ...formState }}
                    onResponse={(blockId, value) => onFieldChange(blockId, value)}
                    readOnly={readOnly}
                  />
                ) : (
                  <MinhaMesaFormFields
                    fields={visibleExecutionFields}
                    formState={formState}
                    onFieldChange={onFieldChange}
                    readOnly={readOnly}
                    invalidFieldIds={invalidFieldIds}
                    variant="execution"
                  />
                )}
              </div>
            )}
          </section>

          <section className="border-t border-slate-200/80 pt-4 dark:border-slate-700">
            <div className="mb-2 flex items-center gap-2">
              <MessageSquarePlus size={13} className="text-slate-400" />
              <h2 className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Registrar · evidências</h2>
            </div>
            {detailLoading ? (
              <div className="animate-pulse h-12 rounded bg-slate-200/60 dark:bg-slate-800" />
            ) : (
              <MinhaMesaCompletionBar
                completionType={task.completionType}
                exitOptions={task.exitOptions}
                checklist={checklist}
                checklistState={checklistState}
                notes={notes}
                onNotesChange={onNotesChange}
                onComplete={onComplete}
                onApprove={onApprove}
                onReject={onReject}
                isCompleting={isCompleting}
                isDisabled={detailLoading}
                canComplete={canComplete}
                onSaveNotes={onSaveNotes}
                dense
                notesOnly
                hideBlockersBanner
                attachInputRef={fileRef}
                executionMode
              />
            )}
          </section>
        </div>
      </div>

      {activityOpen && (
      <aside className="flex w-[220px] shrink-0 flex-col border-l border-slate-200/80 bg-white dark:border-slate-800 dark:bg-[var(--bg-elevated)]">
        <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
          <p className="text-[8px] font-bold uppercase tracking-wider text-slate-400">Atividade</p>
          <div className="mt-2 flex gap-3">
            <div className="flex items-center gap-1 text-[9px] text-slate-500">
              <MessageSquarePlus size={11} />
              <span className="font-bold tabular-nums">{observationsCount}</span>
            </div>
            <div className="flex items-center gap-1 text-[9px] text-slate-500">
              <Paperclip size={11} />
              <span className="font-bold tabular-nums">{attachmentsCount}</span>
            </div>
            {hasStarted && canComplete && !isTaskCompleted && (
              <div className="flex items-center gap-1 text-[9px] text-emerald-600">
                <CheckCircle2 size={11} />
                <span className="font-bold">Pronto</span>
              </div>
            )}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <p className="px-3 pt-2 text-[8px] font-bold uppercase tracking-wider text-slate-400">Movimentações</p>
          <ExecutionTimelineSummary events={timelineEvents} compact />
        </div>
      </aside>
      )}
    </div>
  );
};

export default TaskExecutionWorkbench;
